import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import NewsCard from "../components/NewsCard";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { NewsSEO } from "../components/SEO";
import api from "../lib/api";
import logger from "../lib/logger";

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const category = activeCategory === "all" ? null : activeCategory;
        const data = await api.getNews(category, 20);
        setArticles(data);
      } catch (error) {
        logger.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [activeCategory]);

  const categories = [
    { value: "all", label: "All Articles" },
    { value: "landlord_tips", label: "Landlord Tips" },
    { value: "tenant_advice", label: "Tenant Advice" },
    { value: "market_insights", label: "Market Insights" },
  ];

  return (
    <div className="py-12 md:py-20" data-testid="news-page">
      <NewsSEO />
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <h1 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-4">
          News & Updates
        </h1>
        <p className="text-slate-600 text-lg max-w-2xl">
          Property insights, landlord tips, and tenant advice from the West Yorkshire rental market.
        </p>
      </div>

      {/* Category Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="bg-slate-100 p-1">
            {categories.map((cat) => (
              <TabsTrigger
                key={cat.value}
                value={cat.value}
                className="data-[state=active]:bg-navy data-[state=active]:text-white"
                data-testid={`news-tab-${cat.value}`}
              >
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 skeleton rounded-lg" />
            ))}
          </div>
        ) : articles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-slate-50 rounded-lg">
            <h3 className="font-heading text-xl font-semibold text-navy mb-2">No articles found</h3>
            <p className="text-slate-500 mb-6">Check back soon for the latest property news and tips.</p>
            <Button
              onClick={async () => {
                await api.seedDatabase();
                window.location.reload();
              }}
              className="bg-navy hover:bg-navy-light"
              data-testid="seed-news-btn"
            >
              Load Sample Articles
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
